import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';

import { AvatarRes } from 'hooks';
import { request } from 'utils';
import { NFT, NFTCollection } from './types';

type NftRes = {
  collection_dict: Record<string, NFTCollection>;
  token_list: NFT[];
};

const groupNft = ({ collection_dict, token_list }: NftRes, chain: string) => {
  return token_list
    .filter(x => !chain || !x.chain || x.chain === chain)
    .reduce(
      (m, n) => {
        const collectionId = n.collection_id || '';
        m[collectionId] = m[collectionId] || {
          ...collection_dict[collectionId],
          chain: n.chain,
          price: 0,
          tokens: [],
        };

        m[collectionId].price += (n.usd_price || 0) * n.amount;
        m[collectionId].tokens.push(n);

        return m;
      },
      {} as Record<string, NFTCollection>,
    );
};

export const useQueryNft = (addr: string, chain: string) => {
  const select = useCallback((arg: NftRes) => groupNft(arg, chain), [chain]);

  const result = useQuery(
    ['nft', addr],
    () => request.get<NftRes>(`/nft/list?user_addr=${addr}&is_collection=1`),
    { select },
  );

  const list = useMemo(() => {
    if (!result.data) {
      return [] as NFTCollection[];
    }
    const collections = Object.values(result.data) as NFTCollection[];
    collections.forEach(collection =>
      collection.tokens.sort((x, y) => (y.usd_price || 0) - (x.usd_price || 0)),
    );
    collections.sort((x, y) => y.price - x.price);

    return collections;
  }, [result]);

  return { list, isDone: result.isFetched, isError: result.isError };
};

export type AVATAR_ITEM = {
  id: string;
  chain?: string;
  url?: string;
  thumbnail?: string;
  type?: AvatarRes['content_type'];
};

export const useUpdateAvatarUrl = () => {
  const mutation = useMutation(({ id, chain, url, thumbnail }: AVATAR_ITEM) => {
    const isDefault = id === 'default';
    const isCustom = id === 'custom';

    if (isDefault) {
      return request.post('/user/delete_logo');
    } else if (isCustom) {
      return request.post('/user/update_non_nft_logo', {
        url,
        thumbnail_url: thumbnail,
      });
    }

    const params = {
      nft_id: id,
      chain_id: chain,
    };
    return request.post('/user/update_logo', params);
  });

  return mutation;
};
